import type { DatabaseFile } from '../types';
import { emptyCheckItemMedia } from '../types';

const STORAGE_KEY = 'dailylife.itemDrafts.v1';

export type ItemEditorDraftKind = 'existing' | 'new';

export type ItemComposerDraft = {
  text: string;
  date: string;
};

export type UnsavedItemEdit =
  | {
      kind: 'edit';
      sectionIndex: number;
      itemIndex: number;
      text: string;
      date: string;
    }
  | {
      kind: 'new';
      sectionIndex: number;
      text: string;
      date: string;
    };

type DraftStore = {
  composers: Record<string, ItemComposerDraft>;
  unsaved: Record<string, UnsavedItemEdit[]>;
};

function readStore(): DraftStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { composers: {}, unsaved: {} };
    const parsed = JSON.parse(raw) as Partial<DraftStore>;
    return {
      composers:
        parsed.composers && typeof parsed.composers === 'object' ? parsed.composers : {},
      unsaved: parsed.unsaved && typeof parsed.unsaved === 'object' ? parsed.unsaved : {},
    };
  } catch {
    return { composers: {}, unsaved: {} };
  }
}

function writeStore(store: DraftStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Stable key for in-modal composer text (while the editor is open). */
export function itemComposerDraftKey(
  filePath: string,
  sectionIndex: number,
  kind: ItemEditorDraftKind,
  itemIndex?: number
): string {
  if (kind === 'new') return `${filePath}\n${sectionIndex}\nnew`;
  return `${filePath}\n${sectionIndex}\n${itemIndex ?? 0}`;
}

export function readItemComposerDraft(key: string): ItemComposerDraft | null {
  const draft = readStore().composers[key];
  if (!draft || typeof draft.text !== 'string') return null;
  return { text: draft.text, date: typeof draft.date === 'string' ? draft.date : '' };
}

export function writeItemComposerDraft(key: string, draft: ItemComposerDraft): void {
  const store = readStore();
  store.composers[key] = { text: draft.text, date: draft.date };
  writeStore(store);
}

export function clearItemComposerDraft(key: string): void {
  const store = readStore();
  if (!(key in store.composers)) return;
  delete store.composers[key];
  writeStore(store);
}

function unsavedKey(edit: UnsavedItemEdit): string {
  if (edit.kind === 'new') return `new:${edit.sectionIndex}`;
  return `edit:${edit.sectionIndex}:${edit.itemIndex}`;
}

export function recordUnsavedItemEdit(filePath: string, edit: UnsavedItemEdit): void {
  const store = readStore();
  const list = store.unsaved[filePath] ?? [];
  const key = unsavedKey(edit);
  const next = list.filter(e => unsavedKey(e) !== key);
  next.push(edit);
  store.unsaved[filePath] = next;
  writeStore(store);
}

export function clearUnsavedItemEdits(filePath: string): void {
  const store = readStore();
  let changed = false;
  if (store.unsaved[filePath]) {
    delete store.unsaved[filePath];
    changed = true;
  }
  const prefix = `${filePath}\n`;
  for (const key of Object.keys(store.composers)) {
    if (key.startsWith(prefix)) {
      delete store.composers[key];
      changed = true;
    }
  }
  if (changed) writeStore(store);
}

function firstLine(text: string): string {
  return text.replace(/\r\n/g, '\n').trim().split('\n')[0]?.trim() ?? '';
}

/** Re-apply item edits that were Done locally but not saved to GitHub. */
export function applyUnsavedItemEdits(file: DatabaseFile): boolean {
  const edits = readStore().unsaved[file.path];
  if (!edits?.length) return false;

  let applied = false;
  for (const edit of edits) {
    const section = file.sections[edit.sectionIndex];
    if (!section) continue;

    if (edit.kind === 'edit') {
      const item = section.items[edit.itemIndex];
      if (!item) continue;
      if (item.text === edit.text && item.date === edit.date) continue;
      item.text = edit.text;
      item.date = edit.date;
      item.raw = '';
      applied = true;
      continue;
    }

    const needle = firstLine(edit.text);
    if (!needle) continue;
    const exists = section.items.some(it => firstLine(it.text) === needle);
    if (exists) continue;

    section.items.unshift({
      raw: '',
      text: edit.text,
      date: edit.date,
      state: 'open',
      ...emptyCheckItemMedia(),
    });
    applied = true;
  }

  return applied;
}
